import { useRef, useCallback, useEffect } from 'react';
import useAuth from '../../useAuth';
import {
  getDeviceInfo,
  formatDeviceInfo,
  type AudioType,
} from '../../../utils/deviceInfo';

interface PlaybackEventsRefs {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  currentContentIdRef: React.MutableRefObject<string | null>;
  isPlayingRef: React.MutableRefObject<boolean>;
  audioTypeRef: React.MutableRefObject<AudioType>;
}

export function usePlaybackEvents(refs: PlaybackEventsRefs) {
  const { audioRef, currentContentIdRef, isPlayingRef, audioTypeRef } = refs;
  const { incrementUserSeconds } = useAuth();

  // Cache device info (doesn't change during session)
  const deviceInfoRef = useRef(getDeviceInfo());

  // Session tracking refs
  const durationCheckpointRef = useRef(0); // Time of last duration calculation (0 = no active session)
  const sessionStartPositionRef = useRef(0);
  const currentPlaylistIdRef = useRef<string | null>(null);
  const lastIncrementTimeRef = useRef(0);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const lastSeekEventTimeRef = useRef(0); // Throttle seek events
  const pendingSeekRef = useRef<number | null>(null); // Store pending seek position for delayed send

  // Set playlist ID for session tracking
  const setPlaylistId = useCallback((playlistId: string | null) => {
    currentPlaylistIdRef.current = playlistId;
  }, []);

  // Track listening time - call from timeupdate to increment user seconds
  const trackListeningTime = useCallback(() => {
    if (!isPlayingRef.current) return;

    const now = Date.now();
    if (lastIncrementTimeRef.current > 0) {
      const delta = (now - lastIncrementTimeRef.current) / 1000;
      if (delta > 0 && delta < 2) {
        // Only count reasonable deltas (< 2s to avoid counting pauses)
        incrementUserSeconds(delta);
      }
    }
    lastIncrementTimeRef.current = now;
  }, [isPlayingRef, incrementUserSeconds]);

  // Silent position save - saves position without creating pause event
  // Used for periodic heartbeat to ensure progress isn't lost
  const savePositionSilently = useCallback(() => {
    if (!currentContentIdRef.current || !audioRef.current) return;
    if (!isPlayingRef.current) return;

    const position = audioRef.current.currentTime;
    const now = Date.now();
    // Calculate incremental duration since last heartbeat (or session start)
    const duration = (now - durationCheckpointRef.current) / 1000;
    // Reset for next heartbeat/stop to avoid double-counting
    durationCheckpointRef.current = now;

    fetch('/api/events/heartbeat', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentId: currentContentIdRef.current,
        position,
        duration,
        playlistId: currentPlaylistIdRef.current,
      }),
    }).catch((err) => {
      console.error('[Audio] Heartbeat failed:', err);
    });
  }, [audioRef, currentContentIdRef, isPlayingRef]);

  // Start heartbeat interval (every 30 seconds)
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) return;
    heartbeatIntervalRef.current = setInterval(savePositionSilently, 30000);
  }, [savePositionSilently]);

  // Stop heartbeat interval
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Playback event: start
  const sendPlaybackStart = useCallback(() => {
    if (!currentContentIdRef.current || !audioRef.current) return;

    durationCheckpointRef.current = Date.now();
    sessionStartPositionRef.current = audioRef.current.currentTime;
    lastIncrementTimeRef.current = Date.now();

    // Start periodic position heartbeat
    startHeartbeat();

    // Include device info with audio type
    const deviceInfo = formatDeviceInfo(
      deviceInfoRef.current,
      audioTypeRef.current
    );

    fetch('/api/events/play', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentId: currentContentIdRef.current,
        position: audioRef.current.currentTime,
        playlistId: currentPlaylistIdRef.current,
        deviceInfo,
      }),
    }).catch((err) => {
      console.error('[Audio] Playback start failed:', err);
    });
  }, [audioRef, currentContentIdRef, audioTypeRef, startHeartbeat]);

  // Playback event: stop
  const sendPlaybackStop = useCallback(() => {
    if (!currentContentIdRef.current || !audioRef.current) return;
    if (durationCheckpointRef.current === 0) return; // Already stopped, don't send duplicate

    // Stop periodic position heartbeat
    stopHeartbeat();

    const duration = (Date.now() - durationCheckpointRef.current) / 1000;
    lastIncrementTimeRef.current = 0; // Reset to avoid counting paused time
    durationCheckpointRef.current = 0; // Reset to prevent duplicate pause events from accumulating time

    fetch('/api/events/pause', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentId: currentContentIdRef.current,
        position: audioRef.current.currentTime,
        duration,
        playlistId: currentPlaylistIdRef.current,
      }),
    }).catch((err) => {
      console.error('[Audio] Playback stop failed:', err);
    });
  }, [audioRef, currentContentIdRef, stopHeartbeat]);

  // Playback event: seek (throttled to prevent spam)
  const sendPlaybackSeek = useCallback(
    (toPosition: number) => {
      if (!currentContentIdRef.current) return;

      const now = Date.now();
      const timeSinceLastSeek = now - lastSeekEventTimeRef.current;
      const SEEK_THROTTLE_MS = 300;

      // Always update the pending seek position
      pendingSeekRef.current = toPosition;

      // If we sent a seek event recently, skip this one (the pending position will be sent later)
      if (timeSinceLastSeek < SEEK_THROTTLE_MS) {
        // Schedule a delayed send if not already scheduled
        setTimeout(() => {
          if (pendingSeekRef.current !== null) {
            const pendingPosition = pendingSeekRef.current;
            pendingSeekRef.current = null;

            const duration =
              (Date.now() - durationCheckpointRef.current) / 1000;

            fetch('/api/events/seek', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                contentId: currentContentIdRef.current,
                fromPosition: sessionStartPositionRef.current,
                toPosition: pendingPosition,
                duration,
                playlistId: currentPlaylistIdRef.current,
              }),
            }).catch((err) => {
              console.error('[Audio] Playback seek failed:', err);
            });

            lastSeekEventTimeRef.current = Date.now();
            durationCheckpointRef.current = Date.now();
            sessionStartPositionRef.current = pendingPosition;
          }
        }, SEEK_THROTTLE_MS - timeSinceLastSeek);
        return;
      }

      // Send immediately
      pendingSeekRef.current = null;
      lastSeekEventTimeRef.current = now;

      const duration = (Date.now() - durationCheckpointRef.current) / 1000;

      fetch('/api/events/seek', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentId: currentContentIdRef.current,
          fromPosition: sessionStartPositionRef.current,
          toPosition,
          duration,
          playlistId: currentPlaylistIdRef.current,
        }),
      }).catch((err) => {
        console.error('[Audio] Playback seek failed:', err);
      });

      // Reset session for new segment
      durationCheckpointRef.current = Date.now();
      sessionStartPositionRef.current = toPosition;
    },
    [currentContentIdRef]
  );

  // Playback event: complete
  const sendPlaybackComplete = useCallback(() => {
    if (!currentContentIdRef.current) return;

    // Stop periodic position heartbeat
    stopHeartbeat();

    fetch('/api/events/complete', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentId: currentContentIdRef.current,
        playlistId: currentPlaylistIdRef.current,
      }),
    }).catch((err) => {
      console.error('[Audio] Playback complete failed:', err);
    });
  }, [currentContentIdRef, stopHeartbeat]);

  // Cleanup heartbeat on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  // Visibility change handler - recover playback when returning from background
  // NOTE: We do NOT send pause on visibility hidden because iOS can play audio in background.
  // Sending pause would create phantom pause events and potentially lose position.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPlayingRef.current) {
        if (!audioRef.current) return;

        // Only try to resume if audio actually paused (iOS may have suspended it)
        if (audioRef.current.paused) {
          // Check if audio source is still valid (readyState >= 2 means HAVE_CURRENT_DATA)
          const sourceValid = audioRef.current.readyState >= 2;

          if (sourceValid) {
            // Source valid, just paused - try to resume
            audioRef.current
              .play()
              .then(() => {
                sendPlaybackStart();
              })
              .catch((err) => {
                console.error(
                  '[Audio] Failed to resume after background:',
                  err
                );
                // Reset state quietly, don't show error toast
                isPlayingRef.current = false;
              });
          } else {
            // Source is stale - reset state, don't attempt resume
            console.warn(
              '[Audio] Source stale on resume (readyState=%d), resetting',
              audioRef.current.readyState
            );
            isPlayingRef.current = false;
          }
        }
        // If not paused, audio is still playing - do nothing
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [audioRef, isPlayingRef, sendPlaybackStart]);

  // Page unload handler - send stop event using sendBeacon
  // pagehide is more reliable on iOS Safari (fires on refresh/navigation)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        isPlayingRef.current &&
        currentContentIdRef.current &&
        audioRef.current &&
        durationCheckpointRef.current > 0 // Guard against uninitialized or already-stopped session
      ) {
        // Stop heartbeat immediately
        stopHeartbeat();

        const duration = (Date.now() - durationCheckpointRef.current) / 1000;
        durationCheckpointRef.current = 0; // Reset to prevent duplicate beacon sends
        const data = JSON.stringify({
          contentId: currentContentIdRef.current,
          position: audioRef.current.currentTime,
          duration,
          playlistId: currentPlaylistIdRef.current,
        });
        const blob = new Blob([data], { type: 'application/json' });
        navigator.sendBeacon('/api/events/pause', blob);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [audioRef, currentContentIdRef, isPlayingRef, stopHeartbeat]);

  return {
    setPlaylistId,
    trackListeningTime,
    sendPlaybackStart,
    sendPlaybackStop,
    sendPlaybackSeek,
    sendPlaybackComplete,
  };
}
